import React from "react";
import { Box, Typography } from "@mui/material";
import { ChartBlockView } from "./ChartBlockView";
import {
  CoverSlideView,
  ExecSummaryView,
  BreakdownView,
  KpiCardsView,
  AiPanelView,
  ActionPlanView,
  CorrelationView,
  ScatterView,
  WeatherImpactView,
  ComparisonView,
  SegmentComparisonView,
} from "./slides";
import type { ReportBlock } from "../api/types";
import type { ReportBranding } from "../utils/branding";

interface SlideBlockViewProps {
  block: ReportBlock;
  clientId: string | null;
  branding?: ReportBranding;
  /** Suppress fixed heights so charts fill the slide (print/present). */
  fill?: boolean;
}

/**
 * Read-only dispatcher: renders any report block by type. Shared by the editor
 * preview, presentation mode, and the print route so all three stay identical.
 * Text primitives (heading/narrative) render inline; every data/slide block
 * delegates to its renderer, which fetches live data bound to the block filters.
 */
export const SlideBlockView: React.FC<SlideBlockViewProps> = ({
  block,
  clientId,
  branding,
  fill,
}) => {
  const brandProps = branding
    ? { primaryColor: branding.primaryColor, chartColor: branding.chartColor }
    : undefined;

  switch (block.type) {
    case "heading":
      return (
        <Typography variant="h4" fontWeight={700}>
          {block.text}
        </Typography>
      );
    case "narrative":
      return (
        <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
          {block.text}
        </Typography>
      );
    case "chart":
      return (
        <Box>
          {block.title && (
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {block.title}
            </Typography>
          )}
          <ChartBlockView block={block} clientId={clientId} fill={fill} />
        </Box>
      );
    case "cover":
      return <CoverSlideView block={block} clientId={clientId} branding={brandProps} />;
    case "execSummary":
      return (
        <ExecSummaryView block={block} clientId={clientId} branding={brandProps} fill={fill} />
      );
    case "breakdown":
      return (
        <BreakdownView block={block} clientId={clientId} branding={brandProps} fill={fill} />
      );
    case "kpiCards":
      return (
        <KpiCardsView block={block} clientId={clientId} branding={brandProps} fill={fill} />
      );
    case "aiPanel":
      return (
        <AiPanelView block={block} clientId={clientId} branding={brandProps} fill={fill} />
      );
    case "actionPlan":
      return <ActionPlanView block={block} clientId={clientId} branding={brandProps} />;
    case "correlation":
      return (
        <CorrelationView block={block} clientId={clientId} branding={brandProps} fill={fill} />
      );
    case "scatter":
      return (
        <ScatterView block={block} clientId={clientId} branding={brandProps} fill={fill} />
      );
    case "weatherImpact":
      return (
        <WeatherImpactView block={block} clientId={clientId} branding={brandProps} fill={fill} />
      );
    case "image":
      return (
        <Box sx={{ width: "100%", textAlign: "center" }}>
          <Box
            component="img"
            src={block.url}
            alt={block.caption ?? ""}
            sx={{
              maxWidth: "100%",
              maxHeight: fill ? 480 : 320,
              objectFit: block.fit ?? "contain",
              borderRadius: 1,
            }}
          />
          {block.caption && (
            <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
              {block.caption}
            </Typography>
          )}
        </Box>
      );
    case "comparison":
      return <ComparisonView block={block} clientId={clientId} branding={brandProps} fill={fill} />;
    case "columns":
      return (
        <Box sx={{ display: "flex", flexDirection: "row", gap: 2, width: "100%", alignItems: "flex-start" }}>
          <Box sx={{ flex: "0 0 calc(50% - 8px)", minWidth: 0 }}>
            {block.left && (
              <SlideBlockView block={block.left} clientId={clientId} branding={branding} fill={false} />
            )}
          </Box>
          <Box sx={{ flex: "0 0 calc(50% - 8px)", minWidth: 0 }}>
            {block.right && (
              <SlideBlockView block={block.right} clientId={clientId} branding={branding} fill={false} />
            )}
          </Box>
        </Box>
      );
    case "segmentComparison":
      return (
        <SegmentComparisonView block={block} clientId={clientId} branding={brandProps} fill={fill} />
      );
    case "pageBreak":
      return null;
    default:
      return null;
  }
};
